import { useCallback, useEffect, useRef, useState } from "react";
import type { Animal, Level } from "./data/animals";
import { drawingsForLevel, drawingLevel, getAnimal } from "./data/animals";
import { masterpieces, isMasterpiece } from "./data/masterpieces";
import { isFriendOnly, earnedFriendPool, friendPool } from "./data/friends";
import { rewardProgress } from "./data/rewards";
import { shapes, isShape } from "./data/shapes";
import { allFlags, isFlag } from "./data/worldcup";
import { HomePage } from "./components/HomePage";
import { ShapesPage } from "./components/ShapesPage";
import { OfflineGuard } from "./components/OfflineGuard";
import { DrawingPlayer } from "./components/DrawingPlayer";
import { FactsPage } from "./components/FactsPage";
import { WhyDrawingPage } from "./components/WhyDrawingPage";
import { PaintingsPage } from "./components/PaintingsPage";
import { FlagsPage } from "./components/FlagsPage";
import { PrivacyPage } from "./components/PrivacyPage";
import { ContactPage } from "./components/ContactPage";
import { TermsPage } from "./components/TermsPage";
import { TrophyPage } from "./components/TrophyPage";
import { VoiceProvider } from "./voice/VoiceProvider";
import { SoundProvider } from "./sound/SoundProvider";
import { SpeakProvider } from "./speech/SpeakProvider";
import { TimeLimitProvider } from "./parental/TimeLimitProvider";
import { AutoPlayProvider } from "./settings/AutoPlayProvider";
import { LocaleSwitcher } from "./components/LocaleSwitcher";
import { useLocale, useT } from "./i18n/LocaleProvider";
import { localizeAnimal } from "./i18n/localize";

const COMPLETED_KEY = "learn-draw:completed";
// Flags used to live in their own set; they now count like any other drawing.
// We still read the old key ONCE to migrate finished flags into `completed`,
// then drop it (see the migration effect on mount).
const LEGACY_FLAGS_KEY = "learn-draw:completed-flags";
const LEVEL_KEY = "learn-draw:level";

function loadSet(key: string): Set<string> {
  try {
    const raw = localStorage.getItem(key);
    return new Set(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    return new Set();
  }
}

/** Finished drawings — flags included. Folds in any flags saved under the old
 *  separate key so existing progress carries over. */
function loadCompleted(): Set<string> {
  const all = loadSet(COMPLETED_KEY);
  for (const id of loadSet(LEGACY_FLAGS_KEY)) all.add(id);
  return all;
}

function loadLevel(): Level {
  const n = Number(localStorage.getItem(LEVEL_KEY));
  return n === 7 || n === 10 ? n : 5;
}

// Each in-app screen is a route on a navigation stack. The browser Back button
// pops the stack (returning to the previous screen) instead of leaving the
// site — important for young children, who could otherwise navigate away with
// a single tap. "home" is always the bottom of the stack.
type Route =
  | { kind: "home" }
  | { kind: "shapes" }
  | { kind: "paintings" }
  | { kind: "flags" }
  | { kind: "facts" }
  | { kind: "why" }
  | { kind: "privacy" }
  | { kind: "contact" }
  | { kind: "terms" }
  | { kind: "trophies" }
  | { kind: "drawing"; animal: Animal };

// Recording/export options read once from the URL:
//   ?export=1                 → clean brand-only look (no app chrome/controls)
//   &speed=slowest|slow|…|9   → preset the pen speed
//   &autoplay=1               → auto-start the hands-free Steady-Pace loop
// So a single deep link like `/?d=dog&export=1&speed=slowest&autoplay=1` is a
// self-contained, click-free clip ready to screen-record for YouTube.
const SPEED_PRESETS: Record<string, number> = {
  slowest: 9,
  slow: 7,
  medium: 5,
  normal: 5,
  fast: 3,
  fastest: 1,
};
function readExportParams() {
  const p = new URLSearchParams(window.location.search);
  const raw = p.get("speed");
  const num = raw != null ? Number(raw) : NaN;
  const speed =
    (raw ? SPEED_PRESETS[raw.toLowerCase()] : undefined) ??
    (Number.isFinite(num) && num >= 1 && num <= 9 ? num : undefined);
  return { on: p.has("export"), thumb: p.has("thumb"), speed, autoplay: p.has("autoplay") };
}

export function App() {
  const { locale } = useLocale();
  const t = useT();
  // Drawings the child has finished (flags included) — shown with a green check.
  const [completed, setCompleted] = useState<Set<string>>(loadCompleted);
  const [level, setLevelState] = useState<Level>(loadLevel);
  // YouTube/export view options (see readExportParams above), captured on load.
  const [exportOpts] = useState(readExportParams);

  // One-time migration: fold any flags saved under the old separate key into the
  // main set's storage, then drop the old key.
  useEffect(() => {
    if (localStorage.getItem(LEGACY_FLAGS_KEY) == null) return;
    try {
      localStorage.setItem(COMPLETED_KEY, JSON.stringify([...loadCompleted()]));
      localStorage.removeItem(LEGACY_FLAGS_KEY);
    } catch {
      /* ignore storage errors */
    }
  }, []);

  const [stack, setStack] = useState<Route[]>([{ kind: "home" }]);
  const current = stack[stack.length - 1];
  const previous = stack[stack.length - 2];

  // Mirror the stack depth in the browser's history so the Back/Forward
  // buttons (and Android's hardware back) move through our screens.
  const depthRef = useRef(0);

  useEffect(() => {
    // Establish a base history entry for the home screen.
    window.history.replaceState({ depth: 0 }, "");
    const onPop = (e: PopStateEvent) => {
      const depth =
        e.state && typeof e.state.depth === "number" ? e.state.depth : 0;
      depthRef.current = depth;
      // Truncate the stack to match where history landed.
      setStack((prev) => prev.slice(0, depth + 1));
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  // Navigate forward into a new screen (adds a history entry).
  const push = useCallback((route: Route) => {
    depthRef.current += 1;
    window.history.pushState({ depth: depthRef.current }, "");
    setStack((prev) => [...prev, route]);
  }, []);

  // Swap the current screen without growing history (used for auto-advance,
  // so Back returns to where the child started, not through every drawing).
  const replace = useCallback((route: Route) => {
    setStack((prev) => [...prev.slice(0, -1), route]);
  }, []);

  // Go back one screen. Drives the browser so Back button and our own
  // Home/Back buttons share a single code path.
  const goBack = useCallback(() => {
    window.history.back();
  }, []);

  const markCompleted = useCallback((id: string) => {
    // Reward-only "friend" drawings are just for fun and aren't tracked. Every
    // other finished drawing — flags included — counts the same.
    if (isFriendOnly(id)) return;
    setCompleted((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev).add(id);
      try {
        localStorage.setItem(COMPLETED_KEY, JSON.stringify([...next]));
      } catch {
        /* ignore storage errors (e.g. private mode) */
      }
      return next;
    });
  }, []);

  const resetCompleted = useCallback(() => {
    setCompleted(new Set());
    try {
      localStorage.removeItem(COMPLETED_KEY);
      localStorage.removeItem(LEGACY_FLAGS_KEY);
    } catch {
      /* ignore storage errors */
    }
  }, []);

  const setLevel = useCallback((lvl: Level) => {
    setLevelState(lvl);
    try {
      localStorage.setItem(LEVEL_KEY, String(lvl));
    } catch {
      /* ignore storage errors */
    }
  }, []);

  // Deep link: the per-drawing SEO landing pages link into the app — /?d=<id>
  // opens a drawing directly, and /?go=paintings|flags|facts opens a section.
  // Handle it once on mount.
  const deepLinkedRef = useRef(false);
  useEffect(() => {
    if (deepLinkedRef.current) return;
    deepLinkedRef.current = true;
    const params = new URLSearchParams(window.location.search);
    const go = params.get("go");
    if (
      go === "shapes" ||
      go === "paintings" ||
      go === "flags" ||
      go === "facts" ||
      go === "why" ||
      go === "contact"
    ) {
      push({ kind: go });
      return;
    }
    const id = params.get("d");
    if (!id) return;
    const animal =
      getAnimal(id) ??
      shapes.find((s) => s.id === id) ??
      friendPool.find((f) => f.id === id) ??
      masterpieces.find((m) => m.id === id) ??
      allFlags.find((f) => f.id === id);
    if (!animal) return;
    // Match the level so auto-advance stays within the right pool.
    if (getAnimal(id)) setLevel(drawingLevel(animal));
    push({ kind: "drawing", animal });
  }, [push, setLevel]);

  let screen;
  if (current.kind === "drawing") {
    const animal = localizeAnimal(current.animal, locale);
    // The Back button returns to whichever screen we came from, so label it
    // to match (the paintings/flags gallery, the sticker shelf, or home).
    const cameFromShapes = previous?.kind === "shapes";
    const cameFromPaintings = previous?.kind === "paintings";
    const cameFromFlags = previous?.kind === "flags";
    const cameFromTrophies = previous?.kind === "trophies";
    // Auto-advance stays within the collection the drawing came from. Drawing
    // from the sticker shelf (or a friend-only sticker) cycles among the
    // stickers the child has actually EARNED — never one still locked.
    const inStickers = cameFromTrophies || isFriendOnly(animal.id);
    const basePool = inStickers
      ? earnedFriendPool(rewardProgress(completed))
      : cameFromShapes || isShape(animal.id)
        ? shapes
        : isMasterpiece(animal.id)
          ? masterpieces
          : isFlag(animal.id)
            ? allFlags
            : drawingsForLevel(drawingLevel(animal));
    // The player shows the next drawing's name in the celebration, so the pool
    // has to be localized too — not just the drawing being drawn.
    const pool = basePool.map((a) => localizeAnimal(a, locale));
    const homeLabel = cameFromShapes
      ? t.homeShapes
      : cameFromPaintings
        ? t.homePaintings
        : cameFromFlags
          ? t.homeFlags
          : cameFromTrophies
            ? t.homeStickers
            : t.home;
    const homeAria = cameFromShapes
      ? t.backToShapes
      : cameFromPaintings
        ? t.backToPaintings
        : cameFromFlags
          ? t.backToFlags
          : cameFromTrophies
            ? t.backToStickers
            : t.backToHome;
    screen = (
      <DrawingPlayer
        key={animal.id}
        animal={animal}
        pool={pool}
        completed={completed}
        onHome={goBack}
        homeLabel={homeLabel}
        homeAria={homeAria}
        stickerMode={inStickers}
        onComplete={() => markCompleted(animal.id)}
        onGoTo={(next) => replace({ kind: "drawing", animal: next })}
        exportMode={exportOpts.on}
        thumbMode={exportOpts.thumb}
        initialDuration={exportOpts.speed}
        autoStart={exportOpts.autoplay}
      />
    );
  } else if (current.kind === "shapes") {
    screen = (
      <ShapesPage
        onPick={(animal) => push({ kind: "drawing", animal })}
        onHome={goBack}
        completed={completed}
      />
    );
  } else if (current.kind === "paintings") {
    screen = (
      <PaintingsPage
        onPick={(animal) => push({ kind: "drawing", animal })}
        onHome={goBack}
        completed={completed}
      />
    );
  } else if (current.kind === "flags") {
    screen = (
      <FlagsPage
        onPick={(animal) => push({ kind: "drawing", animal })}
        onHome={goBack}
        completed={completed}
      />
    );
  } else if (current.kind === "facts") {
    screen = <FactsPage onHome={goBack} />;
  } else if (current.kind === "why") {
    screen = <WhyDrawingPage onHome={goBack} />;
  } else if (current.kind === "privacy") {
    screen = <PrivacyPage onHome={goBack} />;
  } else if (current.kind === "contact") {
    screen = <ContactPage onHome={goBack} />;
  } else if (current.kind === "terms") {
    screen = <TermsPage onHome={goBack} />;
  } else if (current.kind === "trophies") {
    screen = (
      <TrophyPage
        onHome={goBack}
        completed={completed}
        onReset={resetCompleted}
        onPick={(animal) => push({ kind: "drawing", animal })}
      />
    );
  } else {
    screen = (
      <HomePage
        onPick={(animal) => push({ kind: "drawing", animal })}
        completed={completed}
        level={level}
        onLevelChange={setLevel}
        onOpenShapes={() => push({ kind: "shapes" })}
        onOpenFacts={() => push({ kind: "facts" })}
        onOpenWhy={() => push({ kind: "why" })}
        onOpenPaintings={() => push({ kind: "paintings" })}
        onOpenFlags={() => push({ kind: "flags" })}
        onOpenTrophies={() => push({ kind: "trophies" })}
        onOpenPrivacy={() => push({ kind: "privacy" })}
        onOpenContact={() => push({ kind: "contact" })}
        onOpenTerms={() => push({ kind: "terms" })}
      />
    );
  }

  return (
    <TimeLimitProvider>
      <SoundProvider>
        <SpeakProvider>
          <VoiceProvider>
            <AutoPlayProvider>
              <main>
                {screen}
                {/* Language flag only on the home screen — it shouldn't sit over
                    the drawing/animation page or the other sub-screens. */}
                {!exportOpts.on && current.kind === "home" && <LocaleSwitcher />}
              </main>
              <OfflineGuard />
            </AutoPlayProvider>
          </VoiceProvider>
        </SpeakProvider>
      </SoundProvider>
    </TimeLimitProvider>
  );
}
